import { CircularProgress } from '@mui/material';
import { Card, ListGroup, Form, Row, Col, Table, Modal, Button } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { successToast, errorToast } from 'utils/toastUtils';
import PersonIcon from '@mui/icons-material/Person';
import Download from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import WindowIcon from '@mui/icons-material/Window';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import ShieldIcon from '@mui/icons-material/Shield';
import BeSafeSwitch from 'components/commons/BeSafeSwitch';
import ArgusDeviceInfoModal from './ArgusDeviceInfoModal';
import ArgusLicenseStatus from './ArgusLicenseStatus';
import { isArgusScanningSwitchDisabled } from './argusScanning';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueuedFieldSave } from 'hooks/useQueuedFieldSave';
import { updateNetwork } from 'app/crud/networks.crud';
import NetworksSelect from 'components/configuration/firewall/modals/NetworksSelect';
import {
  sendEmailToUser,
  regenerateApiKey,
  resetTwoFactorAuth,
  updateTwoFactorAuthEnabled,
  updateAutoMode,
  updateArgusScanningEnabled,
  updateArgusMode,
  updateArgusServiceEnabled,
  updateNetworkSla,
  updateNetworkSlaCollectorConfig,
  updateArgusCollectorConfig,
  updateNetflowConfig,
} from 'app/crud/vpn_client_configs.crud';
import { getCertificate } from 'app/crud/certificates.crud';
import CertificateSelect from 'components/commons/CertificateSelect';
import UserCollectorConfig from './UserCollectorConfig';
import {
  Add,
  Delete,
  Edit,
  HelpOutline,
  Remove,
  LaptopMac,
  LaptopWindows,
  PhoneAndroid,
  PhoneIphone,
  SystemUpdateAlt,
  Terminal,
} from '@mui/icons-material';
import BeSafeButton from 'components/commons/BeSafeButton';
import BeSafeTooltip from 'components/commons/BeSafeTooltip';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import { ConfirmationPopup } from 'components/ConfirmDialog';
import axios from 'axios';

import { getWhereUsed, isValidHostname, isPortValid } from 'utils/utils';
import {
  TUNNEL_TYPE_SSL,
  TUNNEL_TYPE_IPSEC,
  TUNNEL_LABEL_SSL,
  TUNNEL_LABEL_IPSEC,
  OS_WINDOWS,
  OS_ANDROID,
  OS_IOS,
  OS_MAC,
  OS_LINUX,
  OS_OTHER,
  ARGUS_MODE_ANTIVIRUS,
  ARGUS_MODE_PROXY,
} from 'utils/constants';

// Azure AD imports are labelled by the product's current name
const SOURCE_LABELS = { Azure: 'Entra ID' };
const sourceLabels = (sources) =>
  [].concat(sources).map((source) => SOURCE_LABELS[source] || source).join(',');

const SLA_PROTOCOLS = ['icmp', 'tcp', 'udp'];
const SLA_PORTLESS_PROTOCOLS = ['icmp'];
const DEFAULT_SLA_PROTOCOL = 'tcp';
const DEFAULT_SLA_PORT = 443;

const isAllowedSlaTarget = (item) => {
  if (item?.object_type === 'fqdn') return true;
  if (item?.object_type === 'network') {
    const masks = [item.target, item.target_secondary].filter(Boolean).map((address) => address.split('/')[1]?.trim());
    if (masks.length === 0) return true;
    return masks.every((mask) => !mask || mask === '32' || mask === '128');
  }
  return false;
};

const isValidSlaPortProtocol = (item) => {
  if (!SLA_PROTOCOLS.includes(item?.protocol)) return false;
  if (SLA_PORTLESS_PROTOCOLS.includes(item.protocol)) return true;
  const port = Number(item.port);
  return Number.isInteger(port) && port >= 1 && port <= 65535;
};

const EMPTY_NETFLOW_CONFIG = {
  enabled: false,
  host: '',
  port: '',
  protocol: 'tcp',
  encrypted: false,
  trust_unknown_ca: false,
  certificate_id: null,
  sampling_rate: 100,
  export_frequency: 60,
};

const UserSubList = ({
  previewLoading,
  users,
  selectedUser,
  deleteNetworkObject,
  showEditNetworkModal,
  refetchNetwork,
}) => {
  const whereUsed = getWhereUsed(selectedUser);
  const [ipsecTunnels, setIpsecTunnels] = useState([]);
  const [sslTunnels, setSslTunnels] = useState([]);
  const [disableRegenerateBtn, setDisableRegenerateBtn] = useState(false);
  const [isTwoFactorAuthEnabled, setIsTwoFactorAuthEnabled] = useState(false);
  const [isAutoModeEnabled, setIsAutoModeEnabled] = useState(false);
  const [isArgusScanningEnabled, setIsArgusScanningEnabled] = useState(false);
  const [enableArgusService, setEnableArgusService] = useState(false);
  const [isArgusServiceUpdating, setIsArgusServiceUpdating] = useState(false);
  const [argusMode, setArgusMode] = useState(ARGUS_MODE_ANTIVIRUS);
  const [argusLicenseInfo, setArgusLicenseInfo] = useState(null);
  const argusLicenseActive = argusLicenseInfo
    ? argusLicenseInfo.has_license
    : true;
  const [infoDevice, setInfoDevice] = useState(null);
  const networkSlaField = useQueuedFieldSave([]);
  const { value: networkSla, setValue: setNetworkSla, save: saveNetworkSlaField, reseed: reseedNetworkSla } = networkSlaField;
  const networkSlaBeforePortEditRef = useRef(null);
  const netflowConfigField = useQueuedFieldSave(EMPTY_NETFLOW_CONFIG);
  const { value: netflowConfig, save: saveNetflowConfigField, reseed: reseedNetflowConfig } = netflowConfigField;
  const [netflowSwitchKey, setNetflowSwitchKey] = useState(0);
  const [netflowModal, setNetflowModal] = useState(null);
  const [netflowModalErrors, setNetflowModalErrors] = useState({});
  const [netflowModalCertificate, setNetflowModalCertificate] = useState(null);
  const intl = useIntl();
  const downloadUrl = `${axios.defaults.baseURL}/windows_clients/download_windows_exe`;
  const deviceStatuses =
    selectedUser?.vpn_client_config?.vpn_client_devices ||
    [];
  const activeDeviceStatuses = deviceStatuses.filter(
    (device) => device.is_active
  );
  const displayDeviceStatuses = activeDeviceStatuses;


  const allTunnels = [...sslTunnels, ...ipsecTunnels];
  const tunnelTypeById = new Map(
    allTunnels.map((tunnel) => [String(tunnel.id), tunnel.tunnel_type])
  );

  const tunnelTypeLabel = (type) => {
    if (type === TUNNEL_TYPE_SSL) return TUNNEL_LABEL_SSL;
    if (type === TUNNEL_TYPE_IPSEC) return TUNNEL_LABEL_IPSEC;
    return (type || '').toUpperCase();
  };

  const OS_CONFIG = [
    {
      key: OS_WINDOWS,
      name: 'Windows',
      matches: ['windows'],
      replaces: ['windows'],
      icon: <WindowIcon fontSize="small" />,
      deviceIcon: <LaptopWindows fontSize="small" />,
    },
    {
      key: OS_ANDROID,
      name: 'Android',
      matches: ['android'],
      replaces: ['android'],
      icon: <AndroidIcon fontSize="small" />,
      deviceIcon: <PhoneAndroid fontSize="small" />,
    },
    {
      key: OS_IOS,
      name: 'iOS',
      matches: ['ios'],
      replaces: ['ios'],
      icon: <AppleIcon fontSize="small" />,
      deviceIcon: <PhoneIphone fontSize="small" />,
    },
    {
      key: OS_MAC,
      name: 'macOS',
      matches: ['mac', 'osx', 'macos'],
      replaces: ['macos', 'osx', 'mac'],
      icon: <AppleIcon fontSize="small" />,
      deviceIcon: <LaptopMac fontSize="small" />,
    },
    {
      key: OS_LINUX,
      name: 'Linux',
      matches: ['linux'],
      replaces: ['linux'],
      icon: <Terminal fontSize="small" />,
      deviceIcon: <Terminal fontSize="small" />,
    },
  ];

  const parseDeviceOs = (value) => {
    const raw = (value || '').toLowerCase();
    const os = OS_CONFIG.find((config) =>
      config.matches.some((match) => raw.includes(match))
    );

    if (os) {
      const version = os.replaces.reduce(
        (acc, str) => acc.replace(str, ''),
        raw
      ).trim();
      return { key: os.key, name: os.name, version };
    }

    return { key: OS_OTHER, name: value || 'OS:Unknown', version: '' };
  };

  const osIcon = (key) => {
    const os = OS_CONFIG.find((config) => config.key === key);
    if (os) {
      return os.icon;
    }
    return (
      <BeSafeTooltip
        tooltip={<FormattedMessage id="DEVICES.DEVICE_INFO_UNAVAILABLE_TOOLTIP" />}
        placement="bottom"
      >
        <HelpOutline fontSize="small" />
      </BeSafeTooltip>
    );
  };

  const deviceIcon = (key) => {
    const os = OS_CONFIG.find((config) => config.key === key);
    if (os) {
      return os.deviceIcon;
    }
    return (
      <BeSafeTooltip
        tooltip={<FormattedMessage id="DEVICES.DEVICE_INFO_UNAVAILABLE_TOOLTIP" />}
        placement="bottom"
      >
        <HelpOutline fontSize="small" />
      </BeSafeTooltip>
    );
  };

  const versionIcon = (value) => {
    if (!value || value === 'Unknown') {
      return (
        <BeSafeTooltip
          tooltip={<FormattedMessage id="DEVICES.DEVICE_INFO_UNAVAILABLE_TOOLTIP" />}
          placement="bottom"
        >
          <HelpOutline fontSize="small" />
        </BeSafeTooltip>
      );
    }
    return <SystemUpdateAlt fontSize="small" />;
  };

  const sendWindowsInstallerEmail = (besafe_object_id) => {
    if (!besafe_object_id) {
      return errorToast({ body: 'PAGE_RELOAD.PAGE_RELOAD', intl: intl });
    }
    sendEmailToUser(besafe_object_id)
      .then(() => {
        successToast({
          body: 'ADMIN_USERS.WINDOWS_EXE_EMAIL_SUCCESS',
          intl: intl,
        });
      })
      .catch((err) => {
        errorToast({ body: err.response.data?.error, intl: intl });
      });
  };

  const handleApiKeyRegenerate = (user_id) => {
    if (!user_id) return;

    setDisableRegenerateBtn(true);
    ConfirmationPopup({
      title: intl.formatMessage({
        id: 'GENERAL.WARNING',
      }),
      description: intl.formatMessage({
        id: 'USER_OBJECTS.CONFIRM_API_REGENERATE',
      }),
      okLabel: intl.formatMessage({
        id: 'GENERAL.OK',
      }),
      cancelLabel: intl.formatMessage({
        id: 'GENERAL.CANCEL',
      }),
      okAction: () => {
        regenerateApiKey(user_id)
          .then(() => {
            refetchNetwork();
            successToast({
              body: 'USER_OBJECTS.API_KEY_REGENERATE_SUCCESS',
              intl: intl,
            });
          })
          .catch((err) => {
            errorToast({ body: err.response?.data?.error, intl: intl });
          });
      },
    });
    setDisableRegenerateBtn(false);
  };

  const handleResetTwoFactorAuth = (user_id) => {
    if (!user_id) return;

    ConfirmationPopup({
      title: intl.formatMessage({
        id: 'GENERAL.WARNING',
      }),
      description: intl.formatMessage({
        id: 'USER_OBJECTS.CONFIRM_RESET_TWO_FACTOR_AUTH',
      }),
      okLabel: intl.formatMessage({
        id: 'GENERAL.OK',
      }),
      cancelLabel: intl.formatMessage({
        id: 'GENERAL.CANCEL',
      }),
      okAction: () => {
        resetTwoFactorAuth(user_id)
          .then(() => {
            refetchNetwork();
            successToast({
              body: 'USER_OBJECTS.RESET_TWO_FACTOR_AUTH_SUCCESS',
              intl: intl,
            });
          })
          .catch((err) => {
            errorToast({ body: err.response?.data?.error, intl: intl });
          });
      },
    });
  };

  const handleUpdateTwoFactorAuthEnabled = (val) => {
    setIsTwoFactorAuthEnabled(val);
    updateTwoFactorAuthEnabled(selectedUser.id, val)
      .then(() => {
        successToast({
          body: 'USER_OBJECTS.EDIT_SUCCESS',
          intl: intl,
        });
      })
      .catch((err) => {
        setIsTwoFactorAuthEnabled(!val);
        errorToast({ body: err.response.data?.error, intl: intl });
      });
  };

  const handleAutoModeToggle = (val) => {
    setIsAutoModeEnabled(val);
    updateAutoMode(selectedUser.id, val)
      .then(() => {
        successToast({
          body: 'USER_OBJECTS.AUTO_MODE_UPDATE_SUCCESS',
          intl: intl,
        });
      })
      .catch((err) => {
        setIsAutoModeEnabled(!val);
        errorToast({ body: err.response?.data?.error, intl: intl });
      });
  };

  const handleArgusScanningToggle = (val) => {
    setIsArgusScanningEnabled(val);
    updateArgusScanningEnabled(selectedUser.id, val)
      .then(() => {
        successToast({
          body: 'USER_OBJECTS.ARGUS_SCANNING_UPDATE_SUCCESS',
          intl: intl,
        });
      })
      .catch((err) => {
        setIsArgusScanningEnabled(!val);
        errorToast({ body: err.response?.data?.error, intl: intl });
      });
  };

  const handleArgusServiceToggle = (val) => {
    setEnableArgusService(val);
    setIsArgusServiceUpdating(true);
    updateArgusServiceEnabled(selectedUser.id, val)
      .then(() => {
        // Seats are tenant-wide, so a refetch would reselect another user; adjust locally.
        setArgusLicenseInfo((info) => {
          if (!info) return info;
          const usedSeats = Math.max(info.used_seats + (val ? 1 : -1), 0);
          return {
            ...info,
            used_seats: usedSeats,
            available_seats: Math.max(info.total_seats - usedSeats, 0),
          };
        });
        successToast({
          body: 'USER_OBJECTS.ARGUS_SERVICE_UPDATE_SUCCESS',
          intl: intl,
        });
      })
      .catch((err) => {
        setEnableArgusService(!val);
        errorToast({ body: err.response?.data?.error, intl: intl });
      })
      .finally(() => setIsArgusServiceUpdating(false));
  };

  const handleArgusModeChange = (val) => {
    const previousMode = argusMode;
    setArgusMode(val);
    updateArgusMode(selectedUser.id, val)
      .then(() => {
        successToast({
          body: 'USER_OBJECTS.ARGUS_MODE_UPDATE_SUCCESS',
          intl: intl,
        });
      })
      .catch((err) => {
        setArgusMode(previousMode);
        errorToast({ body: err.response?.data?.error, intl: intl });
      });
  };

  const handleNetworkSlaSave = (slaToSave, previousSla) => {
    if (slaToSave?.length > 0) {
      const hasInvalidTarget = slaToSave.some((item) => !isAllowedSlaTarget(item));
      if (hasInvalidTarget) {
        errorToast({ body: 'VPNCLIENTCONFIG.NETWORK_SLA_SINGLE_IP_ONLY', intl });
        setNetworkSla(previousSla);
        return;
      }
      const hasInvalidPortProtocol = slaToSave.some((item) => !isValidSlaPortProtocol(item));
      if (hasInvalidPortProtocol) {
        errorToast({ body: 'VPNCLIENTCONFIG.NETWORK_SLA_INVALID_PORT_PROTOCOL', intl });
        setNetworkSla(previousSla);
        return;
      }
    }
    saveNetworkSlaField((value) => updateNetworkSla(selectedUser.id, value), slaToSave, {
      userId: selectedUser.id,
      previous: previousSla,
      refetchNetwork,
    });
  };
  const validateNetflowConfig = (config) => {
    const errors = {};
    const required = intl.formatMessage({ id: 'GENERAL.REQUIRED_FIELD' });
    const host = `${config.host ?? ''}`.trim();
    if (!host) errors.host = required;
    else if (!isValidHostname(host)) errors.host = intl.formatMessage({ id: 'PROTOCOL.WRONG_HOST' });

    if (`${config.port ?? ''}`.trim() === '') errors.port = required;
    else if (!/^\d+$/.test(`${config.port}`) || !isPortValid(config.port)) errors.port = intl.formatMessage({ id: 'PROTOCOL.WRONG_PORT' });

    if (config.encrypted && !config.trust_unknown_ca && !config.certificate_id) errors.certificate_id = required;

    const samplingRate = Number(config.sampling_rate);
    if (`${config.sampling_rate ?? ''}`.trim() === '') errors.sampling_rate = required;
    else if (Number.isNaN(samplingRate) || samplingRate < 0.01 || samplingRate > 100) errors.sampling_rate = intl.formatMessage({ id: 'USER_OBJECTS.NETFLOW_INVALID_SAMPLING_RATE' });

    const exportFrequency = Number(config.export_frequency);
    if (`${config.export_frequency ?? ''}`.trim() === '') errors.export_frequency = required;
    else if (!Number.isInteger(exportFrequency) || exportFrequency < 1 || exportFrequency > 86400) errors.export_frequency = intl.formatMessage({ id: 'USER_OBJECTS.NETFLOW_INVALID_EXPORT_FREQUENCY' });

    return errors;
  };

  const saveNetflowConfig = (next) =>
    saveNetflowConfigField(
      (value) =>
        updateNetflowConfig(selectedUser.id, {
          enabled: value.enabled,
          host: `${value.host}`.trim() || null,
          port: `${value.port}` === '' ? null : Number(value.port),
          protocol: value.protocol,
          encrypted: value.encrypted,
          trust_unknown_ca: value.trust_unknown_ca,
          certificate_id: value.encrypted && !value.trust_unknown_ca ? value.certificate_id : null,
          sampling_rate: Number(value.sampling_rate),
          export_frequency: Number(value.export_frequency),
        }),
      next,
      { userId: selectedUser.id, refetchNetwork }
    );

  const saveNetflowEnabled = (enabled) =>
    saveNetflowConfigField(
      () => updateNetflowConfig(selectedUser.id, { enabled }),
      { ...netflowConfig, enabled },
      { userId: selectedUser.id, refetchNetwork }
    );

  const openNetflowModal = (enabled = netflowConfig.enabled) => {
    setNetflowModalErrors({});
    setNetflowModalCertificate(null);
    setNetflowModal({ ...netflowConfig, enabled });
    if (netflowConfig.certificate_id) {
      getCertificate(netflowConfig.certificate_id)
        .then((response) => setNetflowModalCertificate(response.data.certificate || null))
        .catch(() => errorToast({ body: 'SSL_INSPECTION.CERTIFICATE_ERROR_FETCH', intl }));
    }
  };

  const closeNetflowModal = () => {
    setNetflowModal(null);
    setNetflowModalErrors({});
    setNetflowSwitchKey((key) => key + 1);
  };

  const updateNetflowModal = (changes) => {
    setNetflowModal((prev) => ({ ...prev, ...changes }));
    setNetflowModalErrors((prev) => ({ ...prev, ...Object.fromEntries(Object.keys(changes).map((key) => [key, null])) }));
  };

  const handleNetflowModalSave = () => {
    const errors = validateNetflowConfig(netflowModal);
    setNetflowModalErrors(errors);
    if (Object.keys(errors).length > 0) return;

    saveNetflowConfig({ ...netflowModal, host: netflowModal.host.trim() }).then((saved) => {
      if (saved) closeNetflowModal();
    });
  };

  const handleNetflowToggle = (enabled) => {
    if (enabled && Object.keys(validateNetflowConfig(netflowConfig)).length > 0) {
      openNetflowModal(true);
      return;
    }
    saveNetflowEnabled(enabled);
  };

  const userIsActive = (user) => {
    const userIsNonEditable = !user.editable;
    const userDataExists = 'data' in user;
    const activeKeyExists = 'active' in user.data;
    return (
      userIsNonEditable && userDataExists && activeKeyExists && user.data.active
    );
  };

  const detachIPV4Address = (index) => {
    let newIpv4s = [...selectedUser.data?.ipv4_addresses];
    newIpv4s.splice(index, 1);

    let params = {
      name: selectedUser.name,
      email: selectedUser.data?.email,
      group: selectedUser.data?.group,
      domain: selectedUser.data?.domain,
      computer: selectedUser.data?.computer,
      ipv4_addresses: newIpv4s,
      ipv6_addresses: selectedUser.data?.ipv6_addresses,
      mac_addresses: selectedUser.data?.mac_addresses,
      source: selectedUser.data?.source,
      type: 'user',
    };

    updateUser(params);
  };

  const detachIPV6Address = (index) => {
    let newIpv6s = [...selectedUser.data?.ipv6_addresses];
    newIpv6s.splice(index, 1);

    let params = {
      name: selectedUser.name,
      email: selectedUser.data?.email,
      group: selectedUser.data?.group,
      domain: selectedUser.data?.domain,
      computer: selectedUser.data?.computer,
      ipv4_addresses: selectedUser.data?.ipv4_addresses,
      ipv6_addresses: newIpv6s,
      mac_addresses: selectedUser.data?.mac_addresses,
      source: selectedUser.data?.source,
      type: 'user',
    };

    updateUser(params);
  };

  const detachMacAddress = (index) => {
    let newMacs = [...selectedUser.data?.mac_addresses];
    newMacs.splice(index, 1);

    let params = {
      name: selectedUser.name,
      email: selectedUser.data?.email,
      group: selectedUser.data?.group,
      domain: selectedUser.data?.domain,
      computer: selectedUser.data?.computer,
      ipv4_addresses: selectedUser.data?.ipv4_addresses,
      ipv6_addresses: selectedUser.data?.ipv6_addresses,
      mac_addresses: newMacs,
      source: selectedUser.data?.source,
      type: 'user',
    };

    updateUser(params);
  };

  const updateUser = (params) => {
    updateNetwork({ ...params, id: selectedUser.id })
      .then(() => {
        refetchNetwork();

        successToast({
          body: 'USER_OBJECTS.EDIT_SUCCESS',
          intl: intl,
        });
      })
      .catch((err) => {
        errorToast({ body: err.response.data?.error, intl: intl });
      });
  };

  const extractRemoteTunnelsByType = useCallback(
    (type) => {
      return selectedUser?.vpn_tunnels
        ?.concat(selectedUser?.besafe_groups?.flatMap((obj) => obj.vpn_tunnels))
        ?.filter(
          (tunnel, index, self) =>
            index === self.findIndex((t) => t.id === tunnel.id)
        )
        ?.filter((tunnel) => tunnel.tunnel_type === type);
    },
    [selectedUser]
  );

  useEffect(() => {
    if (selectedUser.id) {
      setSslTunnels(extractRemoteTunnelsByType(TUNNEL_TYPE_SSL));
      setIpsecTunnels(extractRemoteTunnelsByType(TUNNEL_TYPE_IPSEC));
      setIsTwoFactorAuthEnabled(
        selectedUser?.vpn_client_config?.is_two_factor_auth_enabled ?? true
      );
      setIsAutoModeEnabled(
        selectedUser?.vpn_client_config?.is_auto_mode_enabled || false
      );
      setIsArgusScanningEnabled(
        selectedUser?.vpn_client_config?.is_argus_scanning_enabled || false
      );
      setEnableArgusService(
        selectedUser?.vpn_client_config?.enable_argus_service ?? false
      );
      setArgusMode(
        selectedUser?.vpn_client_config?.argus_mode || ARGUS_MODE_ANTIVIRUS
      );
      setArgusLicenseInfo(
        selectedUser?.vpn_client_config?.argus_license_summary || null
      );
      // reseed() only actually reseeds when the selected user changes - this
      // effect also re-fires on any new selectedUser reference for the SAME
      // user (e.g. a refetch triggered by an unrelated save), which would
      // otherwise discard an in-progress, unsaved Network SLA edit.
      reseedNetworkSla(selectedUser?.vpn_client_config?.network_sla_objects_with_config || [], selectedUser.id);
    }
  }, [selectedUser.id, selectedUser, extractRemoteTunnelsByType, reseedNetworkSla]);

  const serverNetflowSettings = selectedUser?.vpn_client_config?.netflow_settings;
  const serverNetflowSettingsKey = JSON.stringify(serverNetflowSettings || null);

  useEffect(() => {
    const settings = JSON.parse(serverNetflowSettingsKey);
    reseedNetflowConfig(
      settings
        ? {
            enabled: !!settings.enabled,
            host: settings.host || '',
            port: settings.port || '',
            protocol: settings.protocol || 'tcp',
            encrypted: !!settings.encrypted,
            trust_unknown_ca: !!settings.trust_unknown_ca,
            certificate_id: settings.certificate_id || null,
            sampling_rate: settings.sampling_rate ?? 100,
            export_frequency: settings.export_frequency ?? 60,
          }
        : EMPTY_NETFLOW_CONFIG,
      selectedUser.id
    );
  }, [selectedUser.id, serverNetflowSettingsKey, reseedNetflowConfig]);

  const handleDownloadClick = async () => {
    try {
      const response = await fetch(downloadUrl);

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || 'Download request failed');
      }

      window.open(json.download_url, '_blank');

      successToast({
        body: 'AGENT_MAILER.DOWNLOAD_SUCCESS',
        intl,
      });
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error.message;

      errorToast({
        body: errorMessage,
        intl,
      });
    }
  };

  return (
    <>
      {previewLoading ? (
        <div className="d-flex card justify-content-center align-items-center w-60 h-100 mr3">
          <CircularProgress />
        </div>
      ) : users.length <= 0 ? (
        <span className="card w-60 h-100 mr-3 p-3 text-center pt-5">
          <FormattedMessage id="GENERAL.NO_DATA" />
        </span>
      ) : (
        <Card className="w-60 h-100 mr-3">
          <Card.Header>
            <Card.Title className="d-flex justify-content-between py-0 my-0 text-cus-heading">
              <span className="my-auto">
                <PersonIcon className="mr-2" />
                {selectedUser.name}
              </span>
              {(selectedUser.editable || !userIsActive(selectedUser)) && (
                <div>
                  {selectedUser.editable && (
                    <BeSafeButton
                      variant="transparent"
                      className="p-0 px-1"
                      onClick={showEditNetworkModal}
                      icon={<Edit className="icon-table-md" />}
                      tooltip="GENERAL.EDIT_SMALL"
                    />
                  )}
                  <BeSafeButton
                    variant="transparent"
                    className="p-0 px-1"
                    onClick={deleteNetworkObject}
                    icon={<Delete className="icon-table-md" />}
                    tooltip="GENERAL.DELETE_SMALL"
                  />
                </div>
              )}
            </Card.Title>
          </Card.Header>
          <Card.Body className="overflow-auto">
            <div className="d-flex py-2">
              <span className="font-weight-bold">
                <FormattedMessage id="NETWORK_OBJECTS.DOMAIN" />
              </span>
              <span>{`: ${selectedUser?.data?.domain || ''}`}</span>
            </div>

            <div className="d-flex py-2">
              <span className="font-weight-bold">
                <FormattedMessage id="NETWORK_OBJECTS.GROUP" />
              </span>
              <span>{`: ${selectedUser?.data?.group || ''}`}</span>
            </div>

            <div className="d-flex py-2">
              <span className="font-weight-bold">
                <FormattedMessage id="NETWORK_OBJECTS.HOSTNAME" />
              </span>
              <span>{`: ${selectedUser?.data?.computer || ''}`}</span>
            </div>

            <div className="d-flex justify-content-between font-weight-bold py-2">
              <FormattedMessage id="OBJECT_EXPLORER.IPV4_LIST" />
              {selectedUser?.editable && (
                <BeSafeButton
                  variant="transparent"
                  className="p-0 mr-3"
                  onClick={showEditNetworkModal}
                  icon={<Add />}
                  tooltip="OBJECT_MODAL.ADD"
                />
              )}
            </div>
            <ListGroup>
              {selectedUser?.data?.ipv4_addresses?.map((address, index) => (
                <ListGroup.Item
                  key={address}
                  className="d-flex justify-content-between py-0"
                >
                  <span className="w-75 py-2">{address}</span>
                  {selectedUser?.editable && (
                    <div className="my-auto">
                      <BeSafeButton
                        variant="transparent"
                        className="p-0"
                        disabled={false}
                        onClick={() => detachIPV4Address(index)}
                        icon={<Remove />}
                        tooltip="GENERAL.DELETE_SMALL"
                      />
                    </div>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>

            <div className="d-flex justify-content-between font-weight-bold py-2 mt-3">
              <FormattedMessage id="OBJECT_EXPLORER.IPV6_LIST" />
              {selectedUser?.editable && (
                <BeSafeButton
                  variant="transparent"
                  className="p-0 mr-3"
                  onClick={showEditNetworkModal}
                  icon={<Add />}
                  tooltip="OBJECT_MODAL.ADD"
                />
              )}
            </div>
            <ListGroup>
              {selectedUser?.data?.ipv6_addresses?.map((address, index) => (
                <ListGroup.Item
                  key={address}
                  className="d-flex justify-content-between py-0"
                >
                  <span className="w-75 py-2">{address}</span>
                  {selectedUser?.editable && (
                    <div className="my-auto">
                      <BeSafeButton
                        variant="transparent"
                        className="p-0"
                        disabled={false}
                        onClick={() => detachIPV6Address(index)}
                        icon={<Remove />}
                        tooltip="GENERAL.DELETE_SMALL"
                      />
                    </div>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>

            <div className="d-flex justify-content-between font-weight-bold py-2 mt-3">
              <FormattedMessage id="OBJECT_EXPLORER.MAC_ADDRESSES" />
              {selectedUser?.editable && (
                <BeSafeButton
                  variant="transparent"
                  className="p-0 mr-3"
                  onClick={showEditNetworkModal}
                  icon={<Add />}
                  tooltip="OBJECT_MODAL.ADD"
                />
              )}
            </div>
            <ListGroup>
              {selectedUser?.data?.mac_addresses?.map((address, index) => (
                <ListGroup.Item
                  key={address}
                  className="d-flex justify-content-between py-0"
                >
                  <span className="w-75 py-2">{address}</span>
                  {selectedUser?.editable && (
                    <div className="my-auto">
                      <BeSafeButton
                        variant="transparent"
                        className="p-0"
                        disabled={false}
                        onClick={() => detachMacAddress(index)}
                        icon={<Remove />}
                        tooltip="GENERAL.DELETE_SMALL"
                      />
                    </div>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>

            {selectedUser?.data?.source && (
              <div className="d-flex py-2">
                <span className="font-weight-bold">
                  <FormattedMessage id="NETWORK_OBJECTS.SOURCE" />
                </span>
                <span>{`: ${sourceLabels(selectedUser.data.source)}`}</span>
              </div>
            )}

            <div className="d-flex py-2">
              <span className="font-weight-bold">Status</span>
              <span>{`: ${selectedUser?.data?.active ? 'On' : 'Off'}`}</span>
            </div>

            <div className="d-flex py-2 mt-3 mb-3 border-top border-bottom">
              <span className="font-weight-bold text-cus-heading">
                <FormattedMessage id="USER_OBJECTS.BESAFE_VPN_AGENT" />
              </span>
            </div>

            <>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="OBJECT_EXPLORER.USER_BUILD" />:
                  </Form.Label>
                </Col>
                <Col md={8}>
                  <BeSafeButton
                    className="px-1 py-0 mr-1"
                    variant="primary"
                    onClick={handleDownloadClick}
                    icon={<Download className="icon-table" />}
                    disabled={!selectedUser?.vpn_client_config}
                    tooltip="USER_OBJECTS.DOWNLOAD_BUILD"
                  />
                  <BeSafeButton
                    className="px-1 py-0 mr-1 ml-1"
                    variant="primary"
                    onClick={() => sendWindowsInstallerEmail(selectedUser?.id)}
                    icon={<EmailIcon className="icon-table" />}
                    disabled={!selectedUser?.vpn_client_config}
                    tooltip="USER_OBJECTS.SEND_EMAIL"
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="USER_OBJECTS.API_KEY" />
                  </Form.Label>
                </Col>
                <Col md={8} className="d-flex align-items-center">
                  <span>{selectedUser?.vpn_client_config?.api_key || ''}</span>
                  <BeSafeButton
                    className="px-1 py-0 mr-1 ml-2"
                    variant="primary"
                    onClick={() => handleApiKeyRegenerate(selectedUser?.id)}
                    icon={<RefreshIcon className="icon-table" />}
                    disabled={disableRegenerateBtn}
                    tooltip="USER_OBJECTS.REGENERATE_API_KEY"
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="NETWORK_OBJECTS.OBSERVATION_ID" />
                  </Form.Label>
                </Col>
                <Col md={8}>
                  <span>{selectedUser?.data?.observation_id || '-'}</span>
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="USER_OBJECTS.AUTO_CONNECT_MODE" />
                  </Form.Label>
                </Col>
                <Col md={8} className="d-flex align-items-center">
                  <BeSafeSwitch
                    name="auto_mode"
                    state={isAutoModeEnabled}
                    onChange={(val) => handleAutoModeToggle(val)}
                    disabled={!selectedUser?.vpn_client_config}
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="USER_OBJECTS.TWO_FACTOR_AUTH_ENABLED" />
                  </Form.Label>
                </Col>
                <Col md={8} className="d-flex align-items-center">
                  <BeSafeSwitch
                    name="two_factor_auth"
                    state={isTwoFactorAuthEnabled}
                    onChange={(val) => handleUpdateTwoFactorAuthEnabled(val)}
                    disabled={!selectedUser?.editable}
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="USER_OBJECTS.TWO_FACTOR_AUTH_SETUP_COMPLETED" />
                  </Form.Label>
                </Col>
                <Col md={8} className="d-flex align-items-center">
                  <FormattedMessage
                    id={
                      selectedUser?.vpn_client_config?.is_two_factor_auth_setup_completed
                        ? 'GENERAL.YES'
                        : 'GENERAL.NO'
                    }
                  />
                  <BeSafeButton
                    className="px-1 py-0 mr-1 ml-2"
                    variant="primary"
                    onClick={() => handleResetTwoFactorAuth(selectedUser?.id)}
                    icon={<RefreshIcon className="icon-table" />}
                    disabled={!selectedUser?.vpn_client_config?.is_two_factor_auth_setup_completed}
                    tooltip="USER_OBJECTS.RESET_TWO_FACTOR_AUTH"
                  />
                </Col>
              </Form.Group>
              <Form.Group className="mt-3 mb-2">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="VPNCLIENTCONFIG.NETWORK_SLA" defaultMessage="Network SLA" />
                  </Form.Label>
                </div>
                <Form.Text className="text-muted d-block mb-2">
                  <FormattedMessage id="VPNCLIENTCONFIG.NETWORK_SLA_HELPER" defaultMessage="Only single IP addresses (e.g. 8.8.8.8) and FQDN domains are allowed for SLA monitoring." />
                </Form.Text>
                <span>
                  <NetworksSelect
                    disabled={!selectedUser?.editable}
                    handleChange={(val) => {
                      const previousSla = networkSla;
                      const validItems = val.filter(isAllowedSlaTarget);
                      if (validItems.length !== val.length) {
                        errorToast({ body: "VPNCLIENTCONFIG.NETWORK_SLA_SINGLE_IP_ONLY", intl });
                      }
                      const newSla = validItems.map((v) => {
                        const existing = networkSla.find((n) => n.id === v.id);
                        return { ...v, data: { ...v.data, network_type: v.data?.network_type }, protocol: existing?.protocol || v.protocol || DEFAULT_SLA_PROTOCOL, port: existing?.port ?? v.port ?? DEFAULT_SLA_PORT };
                      });
                      setNetworkSla(newSla);
                      handleNetworkSlaSave(newSla, previousSla);
                    }}
                    selectType="source"
                    allowedNetworks={["network", "fqdn"]}
                    networkMaskFilter={32}
                    initialTags={networkSla}
                    ipProtocol="both"
                  />
                </span>
                {networkSla?.length > 0 && (
                  <Table size="sm" className="mt-2 mb-0">
                    <thead>
                      <tr>
                        <th><FormattedMessage id="VPNCLIENTCONFIG.NETWORK_SLA_TARGET" defaultMessage="Target" /></th>
                        <th><FormattedMessage id="VPNCLIENTCONFIG.NETWORK_SLA_PROTOCOL" defaultMessage="Protocol" /></th>
                        <th><FormattedMessage id="VPNCLIENTCONFIG.NETWORK_SLA_PORT" defaultMessage="Port" /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {networkSla.map((item, index) => (
                        <tr key={item.id}>
                          <td className="align-middle">{item.name}</td>
                          <td>
                            <Form.Control
                              as="select"
                              size="sm"
                              disabled={!selectedUser?.editable}
                              value={item.protocol || DEFAULT_SLA_PROTOCOL}
                              onChange={(e) => {
                                const previousSla = networkSla;
                                const updated = [...networkSla];
                                const portless = SLA_PORTLESS_PROTOCOLS.includes(e.target.value);
                                updated[index] = { ...updated[index], protocol: e.target.value, port: portless ? "" : updated[index].port || DEFAULT_SLA_PORT };
                                setNetworkSla(updated);
                                handleNetworkSlaSave(updated, previousSla);
                              }}
                            >
                              {SLA_PROTOCOLS.map((protocolOption) => (
                                <option key={protocolOption} value={protocolOption}>{protocolOption.toUpperCase()}</option>
                              ))}
                            </Form.Control>
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              size="sm"
                              min={1}
                              max={65535}
                              disabled={!selectedUser?.editable || SLA_PORTLESS_PROTOCOLS.includes(item.protocol)}
                              value={item.port ?? ""}
                              onFocus={() => {
                                if (networkSlaBeforePortEditRef.current === null) {
                                  networkSlaBeforePortEditRef.current = networkSla;
                                }
                              }}
                              onChange={(e) => {
                                const updated = [...networkSla];
                                updated[index] = { ...updated[index], port: e.target.value };
                                setNetworkSla(updated);
                              }}
                              onBlur={() => {
                                const previousSla = networkSlaBeforePortEditRef.current;
                                networkSlaBeforePortEditRef.current = null;
                                if (previousSla === null || previousSla === networkSla) return;
                                handleNetworkSlaSave(networkSla, previousSla);
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Form.Group>
              <UserCollectorConfig
                userId={selectedUser.id}
                editable={!!selectedUser?.editable}
                serverConfig={selectedUser?.vpn_client_config?.network_sla_collector_config}
                updateConfig={updateNetworkSlaCollectorConfig}
                listKey="network_sla_collector_configs"
                errorPrefix="VPNCLIENTCONFIG.NETWORK_SLA_COLLECTOR_CONFIG"
                label={{ id: 'VPNCLIENTCONFIG.NETWORK_SLA_COLLECTORS_LABEL', defaultMessage: 'Network SLA syslog collectors' }}
                hint={{ id: 'VPNCLIENTCONFIG.NETWORK_SLA_COLLECTORS_HINT', defaultMessage: 'Network SLA information is only sent to the collectors listed here. Without any collector, it is not sent.' }}
                titleLabel={{ id: 'VPNCLIENTCONFIG.NETWORK_SLA_COLLECTOR_CONFIG', defaultMessage: 'Network SLA Collector Config' }}
                refetchNetwork={refetchNetwork}
              />
              <UserCollectorConfig
                userId={selectedUser.id}
                editable={!!selectedUser?.editable}
                serverConfig={selectedUser?.vpn_client_config?.argus_collector_config}
                updateConfig={updateArgusCollectorConfig}
                listKey="argus_collector_configs"
                errorPrefix="VPNCLIENTCONFIG.ARGUS_COLLECTOR_CONFIG"
                label={{ id: 'VPNCLIENTCONFIG.ARGUS_COLLECTORS_LABEL', defaultMessage: 'Argus syslog collectors' }}
                hint={{ id: 'VPNCLIENTCONFIG.ARGUS_COLLECTORS_HINT', defaultMessage: 'Argus events are always sent to this BeSafe Pro instance. The collectors listed here receive them as well.' }}
                titleLabel={{ id: 'VPNCLIENTCONFIG.ARGUS_COLLECTOR_CONFIG', defaultMessage: 'Argus Collector Config' }}
                refetchNetwork={refetchNetwork}
              />
              <hr className="my-3" />
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={6}>
                  <Form.Label className="mb-0 font-weight-bold text-nowrap">
                    <FormattedMessage id="USER_OBJECTS.NETFLOW_ENABLE" defaultMessage="Enable Netflow" />
                  </Form.Label>
                </Col>
                <Col md={6} className="d-flex align-items-center justify-content-between">
                  <BeSafeSwitch
                    key={netflowSwitchKey}
                    state={netflowConfig.enabled}
                    name="enable_netflow"
                    disabled={!selectedUser?.editable}
                    onChange={handleNetflowToggle}
                  />
                  <Button
                    className="py-1 px-2"
                    aria-label={intl.formatMessage({ id: 'VPNCLIENTCONFIG.EDIT_NETFLOW_CONFIG', defaultMessage: 'Edit NetFlow settings' })}
                    disabled={!selectedUser?.editable}
                    onClick={() => openNetflowModal()}
                  >
                    <EditIcon fontSize="medium" />
                  </Button>
                </Col>
              </Form.Group>
              {netflowConfig.host && (
                <Table size="sm" className="mt-2 mb-4">
                  <thead>
                    <tr>
                      <th><FormattedMessage id="USER_OBJECTS.NETFLOW_HOST" defaultMessage="Host" /></th>
                      <th><FormattedMessage id="USER_OBJECTS.NETFLOW_PROTOCOL" defaultMessage="Protocol" /></th>
                      <th><FormattedMessage id="USER_OBJECTS.NETFLOW_PORT" defaultMessage="Port" /></th>
                      <th><FormattedMessage id="USER_OBJECTS.NETFLOW_SAMPLING_RATE" defaultMessage="Sampling Rate (%)" /></th>
                      <th><FormattedMessage id="USER_OBJECTS.NETFLOW_EXPORT_FREQUENCY" defaultMessage="Export Frequency (seconds)" /></th>
                      <th><FormattedMessage id="USER_OBJECTS.NETFLOW_ENCRYPTED" defaultMessage="Enable Encryption" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{netflowConfig.host}</td>
                      <td>{netflowConfig.protocol.toUpperCase()}</td>
                      <td>{netflowConfig.port}</td>
                      <td>{netflowConfig.sampling_rate}</td>
                      <td>{netflowConfig.export_frequency}</td>
                      <td><FormattedMessage id={netflowConfig.encrypted ? 'GENERAL.YES' : 'GENERAL.NO'} defaultMessage={netflowConfig.encrypted ? 'Yes' : 'No'} /></td>
                    </tr>
                  </tbody>
                </Table>
              )}
              {sslTunnels.length > 0 && (
                <Form.Group className="mt-3 mb-2">
                  <Col md={4} className="pl-0">
                    <span className="mb-2 font-weight-bold">
                      <FormattedMessage id="OBJECT_EXPLORER.SSL_PROFILE" />
                    </span>
                  </Col>
                  <Col md={8} className="pl-0">
                    <div className="d-flex flex-column pl-2 mt-2">
                      {sslTunnels.map((tunnel) => {
                        const isConnected = activeDeviceStatuses.some(
                          (device) => String(device.active_tunnel_profile_id) === String(tunnel.id)
                        );
                        const tunnelStatus = isConnected ? 'up' : (tunnel.status || tunnel.tunnel_status || 'down');
                        const isUp = tunnelStatus === 'up' || tunnelStatus === 'full_up';
                        return (
                          <div key={tunnel.id} className="d-flex align-items-center justify-content-between w-100 mb-2">
                            <span className="flex-grow-1">{tunnel.name || tunnel.id}</span>
                            <span className={`besafe-tunnel-badge ${isUp ? 'active' : ''}`}>
                              <FormattedMessage
                                id={
                                  tunnelStatus === 'up'
                                    ? 'DEVICES.TUNNEL_UP'
                                    : tunnelStatus === 'full_up'
                                    ? 'DEVICES.TUNNEL_FULL_UP'
                                    : 'DEVICES.TUNNEL_DOWN'
                                }
                              />
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </Col>
                </Form.Group>
              )}
              {ipsecTunnels.length > 0 && (
                <Form.Group as={Row} className="mt-3 mb-2">
                  <Col md={4} className="pl-0">
                    <span className="mb-2 font-weight-bold">
                      <FormattedMessage id="OBJECT_EXPLORER.IPSEC_PROFILE" />
                    </span>
                  </Col>
                  <Col md={8} className="pl-0">
                    <div className="d-flex flex-column pl-0">
                      {ipsecTunnels.map((tunnel) => {
                        const isConnected = activeDeviceStatuses.some(
                          (device) => String(device.active_tunnel_profile_id) === String(tunnel.id)
                        );
                        const tunnelStatus = isConnected ? 'up' : (tunnel.status || tunnel.tunnel_status || 'down');
                        const isUp = tunnelStatus === 'up' || tunnelStatus === 'full_up';
                        return (
                          <div key={tunnel.id} className="d-flex align-items-center justify-content-between w-100 mb-2">
                            <span className="font-weight-bold mr-3">
                              <FormattedMessage id="OBJECT_EXPLORER.IPSEC_PROFILE" />:
                            </span>
                            <span className="flex-grow-1">{tunnel.name || tunnel.id}</span>
                            <span className={`besafe-tunnel-badge ${isUp ? 'active' : ''}`}>
                              <FormattedMessage
                                id={
                                  tunnelStatus === 'up'
                                    ? 'DEVICES.TUNNEL_UP'
                                    : tunnelStatus === 'full_up'
                                    ? 'DEVICES.TUNNEL_FULL_UP'
                                    : 'DEVICES.TUNNEL_DOWN'
                                }
                              />
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </Col>
                </Form.Group>
              )}
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <span className="mb-0 font-weight-bold">
                    <FormattedMessage id="OBJECT_EXPLORER.AGENT_STATUS" />
                  </span>
                </Col>
                <Col md={8} className="d-flex justify-content-start align-items-center">
                  {activeDeviceStatuses.length === 0 && (
                    <span className="text-muted">
                      <FormattedMessage id="OBJECT_EXPLORER.INACTIVE" />
                    </span>
                  )}
                </Col>
              </Form.Group>
              {displayDeviceStatuses.length > 0 && (
                <Row className="mx-n2">
                  {displayDeviceStatuses.map((device) => {
                    const osMeta = parseDeviceOs(device.device_os);
                    const osDisplay = device.device_os
                      ? `${osMeta.name}${osMeta.version ? ` ${osMeta.version}` : ''}`
                      : intl.formatMessage({ id: 'OBJECT_EXPLORER.OS_UNKNOWN' });
                    const deviceName = device.device_name || intl.formatMessage({ id: 'OBJECT_EXPLORER.DEVICE_NAME_UNKNOWN' });
                    const appVersion = device.application_version || intl.formatMessage({ id: 'UNKNOWN' });
                    const isActive = !!device.is_active;
                    const tunnelName = device.active_tunnel_name;
                    const tunnelId = String(device.active_tunnel_profile_id || '');
                    const tunnelType = tunnelTypeLabel(
                      tunnelTypeById.get(tunnelId)
                    );
                    const connectedLabel = tunnelName
                      ? intl.formatMessage(
                          { id: 'OBJECT_EXPLORER.CONNECTED_TUNNEL' },
                          { type: tunnelType, name: tunnelName }
                        )
                      : '';
                    const statusLabel = isActive
                      ? intl.formatMessage({ id: 'OBJECT_EXPLORER.RUNNING' })
                      : intl.formatMessage({ id: 'OBJECT_EXPLORER.INACTIVE' });
                    const statusColor = isActive ? '#2E7D32' : '#9E9E9E';
                    const statusDotColor = isActive ? '#2E7D32' : '#BDBDBD';
                    return (
                      <Col xs={12} sm={6} md={6} key={device.device_id || device.device_name} className="mb-3 px-2">
                        <Card className="h-100 shadow-sm" style={{borderRadius: '10px', border: '0.5px solid #949494' }} >
                          <Card.Body style={{ padding: '14px' }}>
                            <div className="d-flex flex-column" style={{ minHeight: 96 }}>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="font-weight-bold" style={{ fontSize: '16px' }}>
                                  {deviceName}
                                </div>
                                <div className="d-flex align-items-center">
                                  <BeSafeButton
                                    variant="transparent"
                                    className="p-0 mr-2 d-flex align-items-center justify-content-center"
                                    style={{
                                      width: 26,
                                      height: 26,
                                      borderRadius: '50%',
                                      backgroundColor: 'rgba(67, 89, 134, 0.1)',
                                    }}
                                    onClick={() => setInfoDevice(device)}
                                    icon={<ShieldIcon style={{ fontSize: 15, color: '#435986' }} />}
                                    tooltip="USER_OBJECTS.ARGUS_STATUS"
                                  />
                                  {deviceIcon(osMeta.key)}
                                </div>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div style={{ fontSize: '14px' }}>
                                  {osDisplay}
                                </div>
                                <div>{osIcon(osMeta.key)}</div>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div style={{ fontSize: '14px' }}>
                                  {`Version: ${appVersion}`}
                                </div>
                                <div>{versionIcon(device.application_version)}</div>
                              </div>
                              <div
                                className="d-flex justify-content-between align-items-center"
                                style={{
                                  visibility: connectedLabel ? 'visible' : 'hidden',
                                }}
                              >
                                <div style={{ fontSize: '14px' }}>
                                  {connectedLabel || '\u00A0'}
                                </div>
                                <div>
                                  <DeviceHubIcon fontSize="small" />
                                </div>
                              </div>
                            </div>
                            <div
                              className={`d-flex align-items-center rounded px-3 py-2 mt-2 ${
                                connectedLabel ? 'justify-content-between' : 'justify-content-center'
                              }`} style={{ backgroundColor: '#F8F9F8', border: '0.5px solid #949494' }}
                            >
                              {connectedLabel && (
                              <div className="d-flex align-items-center" style={{ color: statusDotColor, fontSize: '14px' }}>
                                <span>Connected</span>
                                <span
                                  className="ml-2"
                                    style={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: statusColor,
                                      display: 'inline-block',
                                  }}
                                />
                              </div>
                              )}
                              <div className="d-flex align-items-center" style={{ color: statusColor, fontSize: '14px' }}>
                                <span>{statusLabel}</span>
                                <span
                                  className="ml-2"
                                  style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: statusDotColor,
                                    display: 'inline-block',
                                  }}
                                />
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </>

            <ArgusDeviceInfoModal
              show={!!infoDevice}
              onHide={() => setInfoDevice(null)}
              device={infoDevice}
            />

            <div className="d-flex py-2 mt-3 mb-3 border-top border-bottom">
              <span className="font-weight-bold text-cus-heading">
                <FormattedMessage id="USER_OBJECTS.BESAFE_ARGUS" />
              </span>
            </div>

            <>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="USER_OBJECTS.ARGUS_LICENSE_STATUS" />
                  </Form.Label>
                </Col>
                <Col md={8}>
                  <ArgusLicenseStatus info={argusLicenseInfo} />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="USER_OBJECTS.ARGUS_SERVICE_ENABLED" />
                  </Form.Label>
                </Col>
                <Col md={8} className="d-flex align-items-center">
                  <BeSafeSwitch
                    name="argus_service_enabled"
                    state={enableArgusService}
                    onChange={(val) => handleArgusServiceToggle(val)}
                    disabled={
                      !selectedUser?.vpn_client_config || isArgusServiceUpdating
                    }
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="USER_OBJECTS.ARGUS_SCANNING_ENABLED" />
                  </Form.Label>
                </Col>
                <Col md={8} className="d-flex align-items-center">
                  <BeSafeSwitch
                    name="argus_scanning_enabled"
                    state={isArgusScanningEnabled}
                    onChange={(val) => handleArgusScanningToggle(val)}
                    disabled={isArgusScanningSwitchDisabled({
                      hasConfig: !!selectedUser?.vpn_client_config,
                      serviceEnabled: enableArgusService,
                      licenseActive: argusLicenseActive,
                      scanningEnabled: isArgusScanningEnabled,
                    })}
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="USER_OBJECTS.ARGUS_MODE" />
                  </Form.Label>
                </Col>
                <Col md={8}>
                  <Form.Control
                    as="select"
                    value={argusMode}
                    onChange={(e) => handleArgusModeChange(e.target.value)}
                    disabled={
                      !selectedUser?.vpn_client_config || !enableArgusService
                    }
                    className="w-auto"
                  >
                    <option value={ARGUS_MODE_ANTIVIRUS}>
                      {intl.formatMessage({ id: 'USER_OBJECTS.ARGUS_MODE_ANTIVIRUS' })}
                    </option>
                    <option value={ARGUS_MODE_PROXY}>
                      {intl.formatMessage({ id: 'USER_OBJECTS.ARGUS_MODE_PROXY' })}
                    </option>
                  </Form.Control>
                </Col>
              </Form.Group>
            </>

            {selectedUser?.besafe_groups?.length > 0 && (
              <>
                <div className="d-flex py-2">
                  <span className="font-weight-bold">
                    <FormattedMessage id="NETWORK_OBJECTS.GROUPS" />
                  </span>
                </div>
                <ListGroup>
                  {selectedUser?.besafe_groups?.map((g, index) => (
                    <ListGroup.Item
                      key={g.id}
                      className="d-flex justify-content-between py-0"
                    >
                      <span className="w-75 py-2">{g.name}</span>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </>
            )}
          </Card.Body>
          <Card.Footer className="obj-where-used">
            <p className="font-weight-bold my-0">
              <FormattedMessage id="GENERAL.WHERE_USED" />:
            </p>
            <span>
              {whereUsed?.length <= 0 ? (
                <FormattedMessage id="GENERAL.NOT_IN_USE" />
              ) : (
                whereUsed?.map((r) => r).join(', ')
              )}
            </span>
            {selectedUser?.data?.azure_ad_user_info?.tenant_name && (
              <div className="mt-2">
                <p className="font-weight-bold my-0">
                  <FormattedMessage id="OBJECT_EXPLORER.AZURE_TENANT_NAME" />:
                </p>
                <span className="font-italic">
                  {selectedUser?.data?.azure_ad_user_info?.tenant_name}
                </span>
              </div>
            )}
          </Card.Footer>
        </Card>
      )}
      <Modal show={netflowModal !== null} onHide={closeNetflowModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title><FormattedMessage id="USER_OBJECTS.NETFLOW_SECTION" defaultMessage="NetFlow" /></Modal.Title>
        </Modal.Header>
        {netflowModal && (
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label><FormattedMessage id="USER_OBJECTS.NETFLOW_HOST" defaultMessage="Host" /></Form.Label>
                  <Form.Control
                    type="text"
                    isInvalid={!!netflowModalErrors.host}
                    value={netflowModal.host}
                    onChange={(e) => updateNetflowModal({ host: e.target.value })}
                  />
                  <Form.Control.Feedback type="invalid">{netflowModalErrors.host}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label><FormattedMessage id="USER_OBJECTS.NETFLOW_PROTOCOL" defaultMessage="Protocol" /></Form.Label>
                  <Form.Control as="select" value={netflowModal.protocol} onChange={(e) => updateNetflowModal({ protocol: e.target.value })}>
                    <option value="tcp">TCP</option>
                    <option value="udp">UDP</option>
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label><FormattedMessage id="USER_OBJECTS.NETFLOW_PORT" defaultMessage="Port" /></Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={65535}
                    isInvalid={!!netflowModalErrors.port}
                    value={netflowModal.port}
                    onChange={(e) => updateNetflowModal({ port: e.target.value })}
                  />
                  <Form.Control.Feedback type="invalid">{netflowModalErrors.port}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group as={Row} className="align-items-center mb-3">
              <Col md={4}>
                <Form.Label className="mb-0"><FormattedMessage id="USER_OBJECTS.NETFLOW_ENCRYPTED" defaultMessage="Enable Encryption" /></Form.Label>
              </Col>
              <Col md={8}>
                <BeSafeSwitch
                  state={netflowModal.encrypted}
                  name="netflow_encrypted"
                  onChange={(val) => {
                    updateNetflowModal(val ? { encrypted: true } : { encrypted: false, certificate_id: null });
                    if (!val) setNetflowModalCertificate(null);
                  }}
                />
              </Col>
            </Form.Group>
            {netflowModal.encrypted && (
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0"><FormattedMessage id="USER_OBJECTS.NETFLOW_TRUST_UNKNOWN_CA" defaultMessage="Trust unknown CA" /></Form.Label>
                </Col>
                <Col md={8}>
                  <BeSafeSwitch
                    state={netflowModal.trust_unknown_ca}
                    name="netflow_trust_unknown_ca"
                    onChange={(val) => {
                      updateNetflowModal(val ? { trust_unknown_ca: true, certificate_id: null } : { trust_unknown_ca: false });
                      if (val) setNetflowModalCertificate(null);
                    }}
                  />
                </Col>
              </Form.Group>
            )}
            {netflowModal.encrypted && !netflowModal.trust_unknown_ca && (
              <Form.Group className="mb-3">
                <CertificateSelect
                  handleChange={(certificate) => {
                    updateNetflowModal({ certificate_id: certificate.id });
                    setNetflowModalCertificate(certificate);
                  }}
                  initialTag={netflowModalCertificate}
                />
                {netflowModalErrors.certificate_id && (
                  <div className="invalid-feedback d-block">{netflowModalErrors.certificate_id}</div>
                )}
              </Form.Group>
            )}
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label><FormattedMessage id="USER_OBJECTS.NETFLOW_SAMPLING_RATE" defaultMessage="Sampling Rate (%)" /></Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="100"
                    isInvalid={!!netflowModalErrors.sampling_rate}
                    value={netflowModal.sampling_rate}
                    onChange={(e) => updateNetflowModal({ sampling_rate: e.target.value })}
                  />
                  <Form.Control.Feedback type="invalid">{netflowModalErrors.sampling_rate}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label><FormattedMessage id="USER_OBJECTS.NETFLOW_EXPORT_FREQUENCY" defaultMessage="Export Frequency (seconds)" /></Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="1"
                    isInvalid={!!netflowModalErrors.export_frequency}
                    value={netflowModal.export_frequency}
                    onChange={(e) => updateNetflowModal({ export_frequency: e.target.value })}
                  />
                  <Form.Control.Feedback type="invalid">{netflowModalErrors.export_frequency}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="secondary" onClick={closeNetflowModal}>
            <FormattedMessage id="GENERAL.CANCEL" defaultMessage="Cancel" />
          </Button>
          <Button variant="primary" onClick={handleNetflowModalSave}>
            <FormattedMessage id="GENERAL.SAVE" defaultMessage="Save" />
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserSubList;
